import React from 'react';

const TAG_DISPLAY = {
  // Inflammatory
  gluten: { label: 'Gluten', color: 'red' },
  dairy: { label: 'Dairy', color: 'red' },
  refined_sugar: { label: 'Refined Sugar', color: 'red' },
  red_meat: { label: 'Red Meat', color: 'red' },
  alcohol: { label: 'Alcohol', color: 'red' },
  caffeine: { label: 'Caffeine', color: 'red' },
  processed_food: { label: 'Processed Food', color: 'red' },
  soy: { label: 'Soy', color: 'red' },
  fried_food: { label: 'Fried Food', color: 'red' },
  // Anti-inflammatory
  leafy_greens: { label: 'Leafy Greens', color: 'green' },
  berries: { label: 'Berries', color: 'green' },
  fatty_fish: { label: 'Fatty Fish', color: 'green' },
  turmeric: { label: 'Turmeric', color: 'green' },
  ginger: { label: 'Ginger', color: 'green' },
  olive_oil: { label: 'Olive Oil', color: 'green' },
  nuts_seeds: { label: 'Nuts & Seeds', color: 'green' },
  avocado: { label: 'Avocado', color: 'green' },
  whole_grains: { label: 'Whole Grains', color: 'green' },
  green_tea: { label: 'Green Tea', color: 'green' },
  beets: { label: 'Beets', color: 'green' },
  cinnamon: { label: 'Cinnamon', color: 'green' },
  lentils: { label: 'Lentils', color: 'green' },
};

// Keyword mappings for auto-detection from meal descriptions
const TAG_KEYWORDS = {
  // Inflammatory
  gluten: ['gluten', 'bread', 'pasta', 'wheat', 'flour', 'bagel', 'croissant', 'naan', 'tortilla', 'wrap', 'cereal', 'pancake', 'waffle', 'pizza', 'noodle', 'ramen', 'cracker'],
  dairy: ['dairy', 'cheese', 'milk', 'yogurt', 'cream', 'butter', 'ice cream', 'whey', 'latte', 'cappuccino'],
  refined_sugar: ['sugar', 'candy', 'cake', 'cookie', 'brownie', 'donut', 'doughnut', 'pastry', 'chocolate', 'syrup', 'soda', 'dessert', 'ice cream'],
  red_meat: ['beef', 'steak', 'burger', 'pork', 'lamb', 'bacon', 'sausage', 'ham', 'ribs', 'brisket', 'meatball'],
  alcohol: ['alcohol', 'wine', 'beer', 'cocktail', 'margarita', 'vodka', 'whiskey', 'rum', 'tequila', 'sake', 'champagne'],
  caffeine: ['coffee', 'espresso', 'latte', 'cappuccino', 'americano', 'energy drink', 'matcha'],
  processed_food: ['processed', 'frozen meal', 'instant', 'microwave', 'fast food', 'chips', 'hot dog'],
  soy: ['soy', 'tofu', 'edamame', 'tempeh', 'soy sauce', 'miso'],
  fried_food: ['fried', 'deep fried', 'french fries', 'fries', 'tempura', 'fritter'],
  // Anti-inflammatory
  leafy_greens: ['spinach', 'kale', 'arugula', 'lettuce', 'chard', 'collard', 'greens', 'salad'],
  berries: ['berries', 'blueberry', 'blueberries', 'strawberry', 'strawberries', 'raspberry', 'raspberries', 'blackberry', 'blackberries', 'acai'],
  fatty_fish: ['salmon', 'tuna', 'sardine', 'mackerel', 'herring', 'anchovy', 'trout', 'fish'],
  turmeric: ['turmeric', 'curry', 'golden milk'],
  ginger: ['ginger'],
  olive_oil: ['olive oil'],
  nuts_seeds: ['almond', 'almonds', 'walnut', 'walnuts', 'cashew', 'pistachio', 'pecan', 'chia', 'flax', 'hemp seed', 'sunflower seed', 'pumpkin seed', 'peanut', 'peanut butter', 'nut butter', 'trail mix', 'granola'],
  avocado: ['avocado', 'guacamole'],
  whole_grains: ['quinoa', 'brown rice', 'oats', 'oatmeal', 'farro', 'barley', 'buckwheat', 'millet', 'wild rice'],
  green_tea: ['green tea'],
  beets: ['beet', 'beets', 'beetroot'],
  cinnamon: ['cinnamon'],
  lentils: ['lentil', 'lentils', 'dal', 'daal'],
};

// Patterns that negate a tag (e.g. "gluten-free" should NOT trigger gluten)
const TAG_NEGATIONS = {
  gluten: [/gluten[\s-]*free/i, /\bGF\b/],
  dairy: [/dairy[\s-]*free/i, /\bDF\b/],
};

export function detectTags(description) {
  if (!description) return [];
  const lower = description.toLowerCase();
  const detected = [];
  for (const [tag, keywords] of Object.entries(TAG_KEYWORDS)) {
    // Check if a negation pattern matches — if so, skip this tag
    const negations = TAG_NEGATIONS[tag];
    if (negations && negations.some(re => re.test(description))) {
      continue;
    }
    for (const keyword of keywords) {
      if (lower.includes(keyword)) {
        detected.push(tag);
        break;
      }
    }
  }
  return detected;
}

export default function TagPicker({ selected = [], onChange, label }) {
  const toggle = (tag) => {
    if (selected.includes(tag)) {
      onChange(selected.filter(t => t !== tag));
    } else {
      onChange([...selected, tag]);
    }
  };

  const inflammatoryTags = Object.entries(TAG_DISPLAY).filter(([, v]) => v.color === 'red');
  const antiInflammatoryTags = Object.entries(TAG_DISPLAY).filter(([, v]) => v.color === 'green');

  return (
    <div className="space-y-4">
      {label && <label className="block text-sm font-medium text-white/80">{label}</label>}

      <div>
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">Inflammatory Triggers</p>
        <div className="flex flex-wrap gap-2">
          {inflammatoryTags.map(([key, { label: tagLabel }]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${selected.includes(key)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {tagLabel}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">Anti-Inflammatory</p>
        <div className="flex flex-wrap gap-2">
          {antiInflammatoryTags.map(([key, { label: tagLabel }]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${selected.includes(key)
                  ? 'bg-white/20 text-white ring-2 ring-white/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {tagLabel}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
